import { useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Paper,
  IconButton,
  Divider,
  Card,
  CardContent,
  Tabs,
  Tab,
  InputAdornment,
  Tooltip
} from "@mui/material";
import {
  addBusiness,
  addClientDns,
  generateFromPrompt,
  getBusinessDetails,
  updateBusiness,
  verifyClientDns
} from "../api/apiService";
import { AutoAwesomeOutlined, ContentCopyOutlined } from "@mui/icons-material";

export default function BusinessInfo() {
  const clientId = JSON.parse(localStorage.getItem("clientId"));

  const [tab, setTab] = useState(0);
  const [form, setForm] = useState({
    companyName: "",
    email: "",
    designation: "",
    industry: "",
    companySize: "",
    website: "",
    phone: "",
    country: "",
    state: "",
    city: "",
    businessDetails: "",
    meetingLink: "",
    companyLogo: "",
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(true);
  const [existingId, setExistingId] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [llmLoading, setLlmLoading] = useState(false);

  // sendgrid dns
  const [dnsRecords, setDnsRecords] = useState([]);
  const [dnsVerified, setDnsVerified] = useState(false);
  const [dnsVerifyLoading, setDnsVerifyLoading] = useState(false);

  const dnsObjectToArray = (obj) => {
    if (!obj) return [];
    return Object.values(obj).map((rec) => ({
      type: rec.type,
      host: rec.host,
      value: rec.data,
      valid: rec.valid
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => setSnackbar({ open: true, message: `Copied: ${text}`, severity: "success" }))
      .catch(() => setSnackbar({ open: true, message: "Failed to copy", severity: "error" }));
  };

  const fetchData = useCallback( async () => {
    try {
      const initialForm = {
        companyName: "",
        email: "",
        designation: "",
        industry: "",
        companySize: "",
        website: "",
        phone: "",
        country: "",
        state: "",
        city: "",
        businessDetails: "",
        meetingLink: "",
        companyLogo: "",
      };
      const response = await getBusinessDetails(clientId);
      if (response?.data) {
        setForm({ ...initialForm, ...response.data.businessDetails });
        setExistingId(response.data.id);

        if (response.data.businessDetails?.sendgridDomainId) {
          setDnsRecords(dnsObjectToArray(response.data.businessDetails.dnsRecords));
          setDnsVerified(response.data.businessDetails.dnsVerified || false);
        }

        const details = response.data.businessDetails;
        if (
          !details.companyName ||
          !details.email ||
          !details.designation ||
          !details.industry ||
          !details.companySize ||
          !details.website ||
          !details.phone ||
          !details.city ||
          !details.state ||
          !details.country
        ) {
          setSnackbar({
            open: true,
            message: "No existing business data, Please add your Business details",
            severity: "warning"
          });
          setBusinessInfo(false);
        } else {
          setBusinessInfo(true);
        }
      }
    } catch (error) {
      console.log("No existing business data or error:", error);
      setSnackbar({
        open: true,
        message: "No existing business data",
        severity: "warning"
      });
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchData();
  },[fetchData]);

  const validate = useCallback(() => {
    const newErrors = {};
    Object.entries(form).forEach(([key, value]) => {
      if (!value?.toString().trim()) {
        newErrors[key] = "This field is required";
      }
    });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Enter a valid email";
    }
    return newErrors;
  }, [form]);

  useEffect(() => {
    setErrors(validate());
  }, [touched, validate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const isFormValid = useMemo(() => Object.keys(validate()).length === 0, [validate]);

  const handleSubmit = async () => {
    setSubmitLoading(true);
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    try {
      if (existingId) {
        await updateBusiness(existingId, form);
        setSnackbar({ open: true, message: "Business details updated successfully.", severity: "success" });
      } else {
        await addBusiness({ ...form });
        setSnackbar({ open: true, message: "Business details saved successfully.", severity: "success" });
      }
    } catch (error) {
      console.error("Error submitting business info:", error);
      setSnackbar({ open: true, message: "Failed to save business details.", severity: "error" });
    } finally {
      setSubmitLoading(false);
      fetchData();
    }
  };

  const handleAddDns = async () => {
    try {
      const domain = form.website?.replace(/^https?:\/\//, "");
      //const res = 
      await addClientDns(clientId, domain);
      fetchData();
      // setDnsRecords(dnsObjectToArray(res.data.dnsRecords));
      // setDnsVerified(res.data.dnsVerified || false);

      setSnackbar({
        open: true,
        message: "DNS records added. Please update them in your domain registrar.",
        severity: "info"
      });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: "Failed to add DNS records", severity: "error" });
    }
  };

  const handleVerifyDns = async () => {
    try {
      setDnsVerifyLoading(true);
      const res = await verifyClientDns(clientId);
      fetchData();
      // setDnsVerified(res.data.verified);
      // setDnsRecords(dnsObjectToArray(res.data.dnsRecords));
      setSnackbar({
        open: true,
        message: res.data.verified
          ? "DNS verified successfully!"
          : "DNS records not verified yet. Please check your domain registrar.",
        severity: res.data.verified ? "success" : "info"
      });
      console.log(res);
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Verification failed due to server error. Please try again later.",
        severity: "error"
      });
    } finally {
      setDnsVerifyLoading(false);
    }
  };

  const handleGenerateBusinessDetails = async () => {
    if (!form.website) {
      setSnackbar({ open: true, message: "Please fill website field so that I can generate content about your business", severity: "warning" });
      return;
    }
  
  // const prompt = `
  //   Write a concise, professional "About your business" paragraph in 2-3 sentences with new line after each sentence and using the following details:
  //   Company: ${form.companyName}
  //   Website: ${form.website}
  //   Industry: ${form.industry}
  //   Size: ${form.companySize}
  //   Location: ${form.city}, ${form.state}, ${form.country}
  //   Focus on presenting the business clearly, highlighting its industry and scale, suitable for a website or profile.
  //   and don't mention this About business heading.
  //   `;

    const prompt = `
      Visit and analyze the website: ${form.website}
      Write a concise, professional paragraph (2-3 sentences) about this business.
      Use new lines after each sentence.
      Focus on presenting the business clearly, highlighting its industry and scale.
      Do not include any headings like "About your business".
      No followup message.
    `;


    try {
      setLlmLoading(true);
      const { data } = await generateFromPrompt(prompt);
      setForm(prev => ({ ...prev, businessDetails: data.response }));
    } catch (err) {
      console.error(err);
    } finally {
      setLlmLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box sx={{ mx: "auto", px:{xs:0, sm:2}, py: 0, background: (theme) => theme.palette.gradient.surface, minHeight: '100%' }}>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        textColor="primary"
        indicatorColor="primary"
        sx={{
          mb: 3,
          "& .MuiTab-root": {
            minHeight: 48,
            height: 48,
            px: 4,
            fontSize: '1rem'
          }
        }}
      >
        <Tab label="Business Information" />
        <Tab label="DNS Setup" />
      </Tabs>

      {/* Business Info Form */}
      {tab === 0 && (
        <Card elevation={0} sx={{ borderRadius: 3, p: {xs:0, sm:3}, border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Typography variant="h6" color="primary" fontWeight={700} gutterBottom >
              Business Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              {[
                { name: "companyName", label: "Company" },
                { name: "email", label: "Email" },
                { name: "designation", label: "Job Title" },
                { name: "industry", label: "Industry" },
                { name: "companySize", label: "Company Size" },
                { name: "website", label: "Website" },
                { name: "phone", label: "Phone" },
                { name: "city", label: "City" },
                { name: "state", label: "State" },
                { name: "country", label: "Country" },
                { name: "meetingLink", label: "Meeting Link" },
                { name: "companyLogo", label: "Company Logo URL" }
              ].map(({ name, label }) => (
                <Grid key={name} size={{xs:12, sm:6}}>
                  <TextField
                    fullWidth
                    variant="filled"
                    name={name}
                    label={label}
                    value={form[name]}
                    onChange={handleChange}
                    error={touched[name] && !!errors[name]}
                    helperText={touched[name] && errors[name]}
                    size="small"
                    sx={{
                      "& .MuiFilledInput-root": {
                        borderRadius: 2,
                        backgroundColor: (theme) => theme.palette.grey[50],
                        "&:before": { borderBottom: "none" },
                        "&:hover": { backgroundColor: (theme) => theme.palette.grey[100] },
                        "&.Mui-focused": {
                          backgroundColor: (theme) => theme.palette.common.white,
                          boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}22`,
                        },
                      },
                    }}
                  />
                </Grid>
              ))}

              <Grid size={{xs:12, sm:12}}>
               <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <TextField
                  name="businessDetails"
                  label="About your business"
                  multiline
                  fullWidth
                  rows={8}
                  value={form.businessDetails}
                  onChange={handleChange}
                  error={touched.businessDetails && !!errors.businessDetails}
                  helperText={touched.businessDetails && errors.businessDetails}
                  variant="filled"
                  sx={{
                    "& .MuiFilledInput-root": {
                      borderRadius: 2,
                      backgroundColor: (theme) => theme.palette.grey[50],
                      "&:before": { borderBottom: "none" },
                      "&:hover": { backgroundColor: (theme) => theme.palette.grey[100] },
                      "&.Mui-focused": {
                        backgroundColor: (theme) => theme.palette.common.white,
                        boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}22`,
                      },
                    },
                  }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Auto-generate business info">
                            <IconButton
                              onClick={handleGenerateBusinessDetails}
                              disabled={llmLoading}
                              sx={{
                                position: "absolute",
                                bottom: 8,
                                right: 12,
                                color: "primary.main",
                              }}
                              loading={llmLoading}
                              loadingIndicator={
                                <Typography variant="body1" sx={{ color: "primary.main", fontWeight: 500, marginRight:10, py:1 }}>
                                  AI is writing...
                                </Typography>
                              }
                            >
                              {!llmLoading && (
                                <>
                                  <Typography variant="body1" sx={{ color: "primary.main" ,px:1}}>
                                    Write with AI
                                  </Typography>
                                  <AutoAwesomeOutlined fontSize="medium" />
                                </>
                              )}
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                />
                  {/* <Button
                    variant="outlined"
                    size="small"
                    onClick={handleGenerateBusinessDetails}
                    loading={llmLoading}
                    loadingPosition="end"
                    sx={{ alignSelf: "flex-start", whiteSpace: "nowrap" }}
                  >
                    Auto Generate
                  </Button> */}
                  </Box>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              size="large"
              fullWidth
              sx={{ mt: 3, py: 1.5, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
              onClick={handleSubmit}
              disabled={!isFormValid}
              loading={submitLoading}
              loadingPosition="end"
            >
              {!businessInfo ? "Save Business Info" : "Update Business Info"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* DNS Setup */}
      {tab === 1 && (
        <Card elevation={0} sx={{ borderRadius: 3, p: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Typography variant="h6" color="primary" fontWeight={700} gutterBottom>
              Email Domain Authentication (SendGrid DNS) : { dnsVerified ? "Verified" : "Not yet verified"}
            </Typography>
            <Typography variant="caption"> Note : This will enable system to send emails to leads via sendgrid service from your domain - {form.website?.replace(/^https?:\/\//, "") || ""}</Typography>
            <Divider sx={{ mb: 2 }} />

            {dnsRecords?.length > 0 ? (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Please add the following DNS records to your domain provider: <b> {form.website?.replace(/^https?:\/\//, "") || ""} </b>
                </Typography>

                {dnsRecords.map((rec, idx) => (
                  <Paper
                    key={idx}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: "1px solid",
                      borderColor: (theme) => rec.valid ? theme.palette.success.main : theme.palette.divider,
                      borderRadius: 2,
                      bgcolor: (theme) => rec.valid ? theme.palette.success.light : theme.palette.background.paper,
                      overflow:"auto"
                    }}
                    elevation={2}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2"><b>Type :</b> {rec.type}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2"><b>Name :</b> {rec.host}</Typography>
                      <IconButton size="small" onClick={() => copyToClipboard(rec.host)}>
                        <ContentCopyOutlined fontSize="small" sx={{ color: 'text.secondary' }}/>
                      </IconButton>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2"><b>Value :</b> {rec.value}</Typography>
                      <IconButton size="small" onClick={() => copyToClipboard(rec.value)}>
                        <ContentCopyOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}

                <Button
                  variant="contained"
                  sx={{ mt: 2, textTransform: "none", borderRadius: 2 }}
                  onClick={handleVerifyDns}
                  color={dnsVerified ? "success" : "primary"}
                  disabled={!businessInfo || dnsVerified || dnsVerifyLoading}
                >
                  {dnsVerified ? "Verified" : dnsVerifyLoading ? "Verifying..." : "Verify DNS"}
                </Button>
              </>
            ) : (
              <Button
                variant="outlined"
                sx={{ mt: 2, textTransform: "none", borderRadius: 2 }}
                onClick={handleAddDns}
                disabled={!businessInfo}
              >
                Setup DNS
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
